// @section 1 AccountOverviewLens — Overview + Relationship merged
//
// The default lens on /accounts/:id. Renders the at-a-glance picture of the
// account PLUS the full editable CRM surface that used to live on a separate
// Relationship lens: contacts, deals (with create), activity timeline (with
// log form). Engagements, BD rollup, financials, and notes still live in the
// right column.
//
// Heritage: this absorbs the old AccountRelationshipLens (Slice B) — the
// content overlap was ~100%, so the merger eliminated a wasted top-level tab.

import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  listAccountContacts, createContact,
  listAccountDeals, createDeal,
  listAccountActivities, createActivity,
  type AccountAggregate, type AccountEngagement,
  type Contact, type Deal, type Activity,
} from '../../api';
import { useIsMobile } from '../../lib/useIsMobile';

export default function AccountOverviewLens({ data }: { data: AccountAggregate }) {
  const companyId = data.company.id;
  // @block 1.0 Phone: the working-surface | associations two-pane stacks to one column.
  const isMobile = useIsMobile();

  return (
    <div style={{ display: 'grid', gap: 18, gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.4fr) minmax(0, 1fr)' }}>
      {/* @block 1.1 Left column — the working surface (deals, activity) */}
      <div style={{ display: 'grid', gap: 18 }}>
        <DealsSection companyId={companyId} />
        <ActivitySection companyId={companyId} />
      </div>

      {/* @block 1.2 Right column — associations and rollups */}
      <div style={{ display: 'grid', gap: 18 }}>
        <ContactsSection companyId={companyId} />
        <EngagementsCard engagements={data.engagements} />
        <BdRollupCard bd={data.bd} />
        <FinancialsStub />
        {data.company.notes && <NotesCard notes={data.company.notes} />}
      </div>
    </div>
  );
}

// @section 2 Contacts — account-scoped (no cross-account name linking)
function ContactsSection({ companyId }: { companyId: string }) {
  const [items, setItems] = useState<Contact[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reload = () => listAccountContacts(companyId).then(setItems).catch((e) => setErr(String(e)));
  useEffect(() => { reload(); }, [companyId]);

  return (
    <Card title="Contacts" subtitle={items ? `${items.length} on this account` : 'Loading…'} err={err}>
      {items !== null && (
        <>
          {items.length === 0 ? (
            <Empty>No contacts on this account yet. Add the first one below.</Empty>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {items.map((c) => (
                <li key={c.id} style={{ padding: '6px 0', borderTop: '1px solid #232a37' }}>
                  <div style={{ fontSize: 13 }}>
                    {c.fullName || `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || '—'}
                    {c.title && <span style={{ color: '#6b7a90', marginLeft: 6 }}>· {c.title}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 1 }}>
                    {c.email ? <a href={`mailto:${c.email}`} style={{ color: '#7eb6e8' }}>{c.email}</a> : '—'}
                    {c.phone && <> · {c.phone}</>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <ContactForm companyId={companyId} onCreated={reload} />
          <div style={mutedHint}>
            Contacts here belong to this account only — adding "Andrew Sawa" again on a different account
            creates a separate row, never auto-merged.
          </div>
        </>
      )}
    </Card>
  );
}

function ContactForm({ companyId, onCreated }: { companyId: string; onCreated: () => void }) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <form
      style={formStyle}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!first && !last && !email) return;
        setBusy(true);
        try {
          await createContact({
            companyId,
            firstName: first || undefined,
            lastName: last || undefined,
            email: email || undefined,
          });
          // title is post-create today (no field on the legacy createContact DTO);
          // captured in the form for the operator's mental model but not yet wired.
          setFirst(''); setLast(''); setEmail(''); setTitle('');
          onCreated();
        } finally { setBusy(false); }
      }}
    >
      <input placeholder="First" value={first} onChange={(e) => setFirst(e.target.value)} style={{ width: 90 }} />
      <input placeholder="Last" value={last} onChange={(e) => setLast(e.target.value)} style={{ width: 100 }} />
      <input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ flex: 1, minWidth: 140 }} />
      <input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} style={{ width: 110 }} />
      <button type="submit" disabled={busy || (!first && !last && !email)}>Add</button>
    </form>
  );
}

// @section 3 Deals — kanban-lite grouped by stage + inline create
const STAGES = ['lead', 'qualified', 'proposal', 'negotiation', 'won', 'lost'] as const;

function DealsSection({ companyId }: { companyId: string }) {
  const [items, setItems] = useState<Deal[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reload = () => listAccountDeals(companyId).then(setItems).catch((e) => setErr(String(e)));
  useEffect(() => { reload(); }, [companyId]);

  const grouped = useMemo(() => {
    const m: Record<string, Deal[]> = {};
    for (const s of STAGES) m[s] = [];
    for (const d of items ?? []) (m[d.stage] ??= []).push(d);
    return m;
  }, [items]);

  const open = (items ?? []).filter((d) => d.stage !== 'won' && d.stage !== 'lost');

  return (
    <Card title="Deals" subtitle={items ? `${open.length} open · ${items.length} total` : 'Loading…'} err={err}>
      {items !== null && (
        <>
          {items.length === 0 ? (
            <Empty>No deals on this account yet.</Empty>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {STAGES.map((stage) => {
                const list = grouped[stage] ?? [];
                if (list.length === 0) return null;
                return (
                  <div key={stage}>
                    <div style={stageHeader}>
                      <span>{stage}</span>
                      <span style={{ color: '#6b7a90' }}>{list.length}</span>
                    </div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {list.map((d) => <DealRow key={d.id} deal={d} />)}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
          <DealForm companyId={companyId} onCreated={reload} />
        </>
      )}
    </Card>
  );
}

function DealRow({ deal }: { deal: Deal }) {
  return (
    <li style={{ padding: '6px 8px', borderTop: '1px solid #232a37', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13 }}>{deal.name}</div>
        <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 2 }}>
          {deal.expectedClose ? `expected ${deal.expectedClose}` : 'no expected close'}
          {deal.probability != null && <> · {deal.probability}% probability</>}
        </div>
      </div>
      <div style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {fmtMoney(deal.valueCents)}
      </div>
    </li>
  );
}

function DealForm({ companyId, onCreated }: { companyId: string; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [stage, setStage] = useState<typeof STAGES[number]>('lead');
  const [valueDollars, setValueDollars] = useState('');
  const [expectedClose, setExpectedClose] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <form
      style={formStyle}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setBusy(true);
        try {
          const valueCents = valueDollars ? Math.round(parseFloat(valueDollars) * 100) : undefined;
          await createDeal({
            name: name.trim(),
            companyId,
            stage,
            valueCents,
            expectedClose: expectedClose || undefined,
          });
          setName(''); setValueDollars(''); setExpectedClose(''); setStage('lead');
          onCreated();
        } finally { setBusy(false); }
      }}
    >
      <input placeholder="Deal name" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1 }} />
      <select value={stage} onChange={(e) => setStage(e.target.value as typeof STAGES[number])}>
        {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <input
        type="number" step="0.01" placeholder="value ($)"
        value={valueDollars} onChange={(e) => setValueDollars(e.target.value)} style={{ width: 110 }}
      />
      <input
        type="date" value={expectedClose} onChange={(e) => setExpectedClose(e.target.value)} style={{ width: 140 }}
      />
      <button type="submit" disabled={busy || !name.trim()}>Add</button>
    </form>
  );
}

// @section 4 Activity timeline — calls/emails/meetings/notes + manual log
const ACTIVITY_TYPES = ['call', 'email', 'meeting', 'sms', 'note', 'task', 'other'] as const;

function ActivitySection({ companyId }: { companyId: string }) {
  const [items, setItems] = useState<Activity[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reload = () => listAccountActivities(companyId).then(setItems).catch((e) => setErr(String(e)));
  useEffect(() => { reload(); }, [companyId]);

  return (
    <Card title="Activity" subtitle={items ? `${items.length} touches` : 'Loading…'} err={err}>
      {items !== null && (
        <>
          {items.length === 0 ? (
            <Empty>No activity logged on this account yet.</Empty>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {items.map((a) => (
                <li key={a.id} style={{ padding: '8px 0', borderTop: '1px solid #232a37' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <ActivityTypeBadge t={a.activityType} />
                    <div style={{ fontSize: 13, flex: 1 }}>{a.subject ?? <em style={{ color: '#6b7a90' }}>(no subject)</em>}</div>
                    <span style={{ fontSize: 11, color: '#6b7a90' }}>{new Date(a.occurredAt).toLocaleString()}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <ActivityForm companyId={companyId} onCreated={reload} />
        </>
      )}
    </Card>
  );
}

function ActivityForm({ companyId, onCreated }: { companyId: string; onCreated: () => void }) {
  const [type, setType] = useState<typeof ACTIVITY_TYPES[number]>('note');
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <form
      style={formStyle}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!subject.trim()) return;
        setBusy(true);
        try {
          await createActivity({ companyId, activityType: type, subject: subject.trim() });
          setSubject('');
          onCreated();
        } finally { setBusy(false); }
      }}
    >
      <select value={type} onChange={(e) => setType(e.target.value as typeof ACTIVITY_TYPES[number])}>
        {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <input placeholder="What happened?" value={subject} onChange={(e) => setSubject(e.target.value)} style={{ flex: 1 }} />
      <button type="submit" disabled={busy || !subject.trim()}>Log</button>
    </form>
  );
}

function ActivityTypeBadge({ t }: { t: string }) {
  const tone = t === 'call' ? '#7eb6e8'
    : t === 'email' ? '#a08fd8'
    : t === 'meeting' ? '#6ec78a'
    : t === 'sms' ? '#f0c674'
    : t === 'note' ? '#a3b1c2'
    : t === 'task' ? '#f0a96b'
    : '#6b7a90';
  return (
    <span style={{
      fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700,
      padding: '2px 8px', borderRadius: 3, color: tone, background: `${tone}22`,
      flexShrink: 0,
    }}>{t}</span>
  );
}

// @section 5 Engagements — read-only summary; deep view is in the Delivery lens
function EngagementsCard({ engagements }: { engagements: AccountEngagement[] }) {
  return (
    <Card title="Engagements" subtitle={engagements.length === 0 ? 'None active' : `${engagements.length} active`}>
      {engagements.length === 0 ? (
        <Empty>No active engagements. Once one starts, deliverables surface in the Delivery lens.</Empty>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {engagements.map((e) => (
            <li key={e.id} style={{ padding: '6px 0', borderTop: '1px solid #232a37' }}>
              <div style={{ fontSize: 13 }}>{e.name}</div>
              <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 2 }}>
                {e.status}
                {e.startsOn && <> · {e.startsOn}{e.endsOn ? ` → ${e.endsOn}` : ''}</>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// @section 6 BD rollup — counts + latest offer pointer (offer lives in Delivery now)
function BdRollupCard({ bd }: { bd: AccountAggregate['bd'] }) {
  return (
    <Card title="Business development" subtitle="Facts, asks, findings live on the BD lens">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, fontSize: 13 }}>
        <Mini label="Facts captured" value={bd.factsCount} />
        <Mini label="Open asks" value={bd.asksOpen} />
        <Mini label="To research" value={bd.findingsToResearch} />
      </div>
      {bd.latestOffer ? (
        <div style={{ marginTop: 12, fontSize: 12, color: '#a3b1c2' }}>
          Latest offer: v{bd.latestOffer.version} ({bd.latestOffer.status}){bd.latestOffer.totalCents != null && <> · {fmtMoney(bd.latestOffer.totalCents, 'USD')}</>}
          {bd.latestOffer.sentAt && <> · sent {new Date(bd.latestOffer.sentAt).toLocaleDateString()}</>}
          <span style={{ color: '#6b7a90' }}> · see Delivery for line items</span>
        </div>
      ) : (
        <div style={{ marginTop: 12, fontSize: 12, color: '#6b7a90' }}>No offers drafted yet.</div>
      )}
    </Card>
  );
}

function Mini({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 600 }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6b7a90' }}>{label}</div>
    </div>
  );
}

// @section 7 Financials stub — wires to hq_erp views in a follow-up
function FinancialsStub() {
  return (
    <Card title="Financials" subtitle="Wires to live QBO/staging data in a follow-up">
      <div style={{ fontSize: 12, color: '#6b7a90' }}>
        Cash position, AR/AP aging, and runway will pivot to the active account once the hq_erp views are pointed at
        staging_operations/mdm. Until then this lens shows the existing global ERP placeholder.
      </div>
    </Card>
  );
}

// @section 8 Notes
function NotesCard({ notes }: { notes: string }) {
  return (
    <Card title="Notes">
      <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{notes}</div>
    </Card>
  );
}

// @section 9 Shared bits
function Card({ title, subtitle, err, children }: { title: string; subtitle?: string; err?: string | null; children: React.ReactNode }) {
  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1.5, color: '#a3b1c2' }}>{title}</h3>
        {subtitle && <span style={{ fontSize: 11, color: '#6b7a90' }}>{subtitle}</span>}
      </div>
      {err && <div style={{ color: '#e87e7e', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <em style={{ color: '#6b7a90', fontSize: 13 }}>{children}</em>;
}

function fmtMoney(cents?: number | null, currency: string = 'USD'): string {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toLocaleString()}`;
  }
}

const cardStyle: CSSProperties = {
  border: '1px solid #232a37',
  borderRadius: 8,
  padding: 14,
  background: '#171c26',
};
const formStyle: CSSProperties = {
  marginTop: 12,
  paddingTop: 12,
  borderTop: '1px dashed #232a37',
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
};
const stageHeader: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 8px',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: 1.5,
  color: '#a3b1c2',
  background: '#1d2330',
  borderRadius: 4,
};
const mutedHint: CSSProperties = {
  marginTop: 10,
  fontSize: 11,
  color: '#6b7a90',
  fontStyle: 'italic',
};
